import React, { useState } from 'react';
import { Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import Tooltip from '../../shared/LibraryTooltip';
import { getCommunityCategoryBadgeSx } from '../../../constants/communityCategoryColors';
import { getCommunityCategoryDisplayLabel } from '../../../constants/communityCategories';
import { getEntityAuthorDisplayName } from '../../../utils/userUtils';
import { colors } from '../../../designTokens';
import { resolveCapabilityType } from '../../../constants/builderCategories';
import BuildQualityIndicator from '../../shared/prompt-display/BuildQualityIndicator';
import { getRelevanceChipProps } from './discoveryRelevanceStyles';
import { getDiscoveryMatchDetails, normalizeDiscoveryResultToPrompt } from './discoveryResultUtils';
import { assistantPrimaryButtonSx, assistantSecondaryButtonSx, assistantTextButtonSx } from '../assistantButtonStyles';
import { discoveryCardSx, discoveryTitleSx } from './discoveryCardStyles';
import DiscoveryCardMetadata from './DiscoveryCardMetadata';

export default function DiscoveryCapabilityCard({
  result,
  kind,
  onDiscoveryAction,
  isBusy = false,
  featured = false,
  isInWorkspace = false,
  assistantContext = 'standard',
}) {
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineInstruction, setRefineInstruction] = useState('');
  const prompt = normalizeDiscoveryResultToPrompt(result);
  const item = result.prompt || result.raw || result;
  const chip = getRelevanceChipProps(result.relevance);
  const resolvedAuthorName = getEntityAuthorDisplayName(prompt);
  const rankedOwnerName = getEntityAuthorDisplayName({ author: { name: result.owner } });
  const authorName = resolvedAuthorName !== 'Unknown User'
    ? resolvedAuthorName
    : rankedOwnerName !== 'Unknown User'
      ? rankedOwnerName
      : 'PromptCrew';
  const useCount = prompt.engagement?.uses || prompt.stats?.usageCount || prompt.stats?.uses
    || prompt.usage || prompt.uses || result.uses || 0;
  const matchDetails = getDiscoveryMatchDetails(result, prompt.description);
  const isBuilderContext = assistantContext === 'builder';
  const resolvedCapabilityType = resolveCapabilityType(prompt.capabilityType);
  const CapabilityTypeIcon = resolvedCapabilityType.Icon;
  const category = result.category || item.category;
  const categoryLabel = resolvedCapabilityType.label;

  const handleAction = (action, event) => {
    event?.stopPropagation?.();
    onDiscoveryAction?.({ action, item, result, kind, instruction: event?.instruction || '' });
  };

  return (
    <Paper
      variant="outlined"
      onClick={() => onDiscoveryAction?.({ action: 'open', item, result, kind })}
      sx={{ ...discoveryCardSx, p: featured ? 2 : 1.5, borderColor: featured && chip.level >= 4 ? 'rgba(29, 78, 216, 0.22)' : 'divider' }}
    >
      <Stack spacing={featured ? 1.25 : 0.75}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Tooltip title={category ? `${resolvedCapabilityType.label} capability · ${getCommunityCategoryDisplayLabel(category)}` : `${resolvedCapabilityType.label} capability`} arrow>
            <Chip
              size="small"
              variant="outlined"
              label={(
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <CapabilityTypeIcon sx={{ fontSize: '12px !important', width: 12, height: 12, flexShrink: 0 }} />
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{categoryLabel}</Box>
                </Box>
              )}
              aria-label={`${resolvedCapabilityType.label} capability`}
              sx={{
                ...getCommunityCategoryBadgeSx(category || 'other'),
                maxWidth: '65%',
                '& .MuiChip-label': { display: 'inline-flex', alignItems: 'center', px: 1.25, overflow: 'hidden' },
              }}
            />
          </Tooltip>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, flexShrink: 0 }}>
            {result.sourceScope === 'community' && (
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.6875rem', fontWeight: 500 }}>
                Community
              </Typography>
            )}
            <Tooltip title={`${chip.explanation}${matchDetails.rationale ? ` ${matchDetails.rationale}` : ''}`} arrow describeChild>
              <Chip tabIndex={0} size="small" label={!featured && chip.level >= 5 ? 'Relevance - 5/5' : chip.label} sx={chip.sx} />
            </Tooltip>
          </Box>
        </Box>
        {category && <Typography variant="caption" color="text.secondary">{getCommunityCategoryDisplayLabel(category)}</Typography>}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={(prompt.description || '').trim()} placement="top" arrow describeChild>
            <Typography tabIndex={0} component="h3" sx={{ ...discoveryTitleSx, minHeight: 0, fontWeight: 600 }}>
              {prompt.title || prompt.name}
            </Typography>
            </Tooltip>
            {prompt.description && (
              <Tooltip placement="top" describeChild arrow title={prompt.description.trim()}>
              <Typography
                tabIndex={0}
                variant="body2"
                sx={{
                  mt: 0.45,
                  color: colors.textSecondary,
                  fontSize: '0.78125rem',
                  lineHeight: 1.45,
                  display: '-webkit-box',
                  WebkitLineClamp: featured ? 2 : 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {prompt.description}
              </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
        {matchDetails.rationale && (
          <Box sx={{ pt: featured ? 0.1 : 0 }}>
            <Typography variant="caption" sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: '0.75rem' }}>
              Why it fits
            </Typography>
            <Box component="ul" sx={{ m: 0, mt: 0.45, pl: 2.25, color: colors.textPrimary }}>
              <Typography component="li" variant="body2" sx={{ color: colors.textPrimary, fontSize: '0.8125rem', lineHeight: 1.42 }}>
                {matchDetails.rationale}
              </Typography>
            </Box>
          </Box>
        )}
        {matchDetails.adaptationGap && (
          <Box>
            <Typography variant="caption" sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: '0.75rem' }}>
              Needs adaptation
            </Typography>
            <Box component="ul" sx={{ m: 0, mt: 0.45, pl: 2.25, color: colors.textPrimary }}>
              <Typography component="li" variant="body2" sx={{ color: colors.textPrimary, fontSize: '0.8125rem', lineHeight: 1.42 }}>
                {matchDetails.adaptationGap}
              </Typography>
            </Box>
          </Box>
        )}
        <DiscoveryCardMetadata
          authorName={authorName}
          author={prompt.author}
          uses={useCount}
          favorites={prompt.engagement?.favorites ?? prompt.favorites ?? result.favorites ?? 0}
          updatedAt={prompt.updatedAt || prompt.createdAt || result.updatedAt || result.createdAt}
          trailing={<BuildQualityIndicator buildQuality={result.buildQuality || prompt.buildQuality} />}
        />
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', pt: 0.1 }}>
          <Button
            size="small"
            variant={!isBuilderContext && isInWorkspace ? 'outlined' : featured ? 'contained' : 'outlined'}
            disabled={isBusy}
            onClick={(e) => handleAction(!isBuilderContext && isInWorkspace ? 'remove' : 'add', e)}
            sx={!isBuilderContext && isInWorkspace ? assistantSecondaryButtonSx : featured ? assistantPrimaryButtonSx : assistantSecondaryButtonSx}
          >
            {isBuilderContext ? 'Use in Builder' : isInWorkspace ? 'Remove' : 'Add to Workspace'}
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={isBusy}
            onClick={(e) => {
              e.stopPropagation();
              setRefineOpen((prev) => !prev);
            }}
            sx={assistantTextButtonSx}
          >
            Refine
          </Button>
        </Box>
        {refineOpen && (
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
              gap: 0.75,
              pt: 0.35,
            }}
          >
            <TextField
              size="small"
              placeholder="How do you want to refine this?"
              value={refineInstruction}
              onChange={(event) => setRefineInstruction(event.target.value)}
              aria-label="How do you want to refine this?"
              multiline
              minRows={2}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.8125rem',
                },
              }}
            />
            <Button
              size="small"
              variant="contained"
              disabled={isBusy}
              onClick={(event) => handleAction('refine-capability', {
                stopPropagation: () => event.stopPropagation(),
                instruction: refineInstruction.trim() || 'better match my initial ask',
              })}
              sx={{ ...assistantPrimaryButtonSx, alignSelf: { sm: 'end' }, minHeight: 32 }}
            >
              Review refinement
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
